/* Field-token extraction from formula strings, for the P5 reference sweep
   ("formula tokens via the formula-lint tokenizer" — x-reference-graph, informationField).

   Two dialects coexist in persisted canvases:
   - native/Excel surface: field('Display Name'), getVariable("Form"|"Entry", "Display Name"),
     colField("Sub Display Name"), sumColumn/sumColCol/sumColConst("Entry", "Table", "Col"...)
     — fields referenced by DISPLAY NAME (string literal args).
   - legacy Python lane: {{form.fieldName}} / {{form.fieldName.subPath}} chips — fields
     referenced by FIELDNAME (the storage key).

   Call arguments are walked with a small nesting-aware splitter (the same approach as
   formulaDiagnostics' argCounts) rather than composed regexes — quote handling with
   backreferences does not survive regex embedding. */

const CHIP_REGEX = /\{\{\s*form\.([^}.\s]+)(?:\.([^}\s]+))?\s*\}\}/g;

/* Parse one call of `name` starting at the "(" and return its top-level argument source
   strings (nesting- and string-literal-aware). */
const splitArgs = (text, openParen) => {
  const args = [];
  let depth = 1;
  let start = openParen + 1;
  for (let i = start; i < text.length; i++) {
    const c = text[i];
    if (c === "'" || c === '"') {
      for (i = i + 1; i < text.length; i++) {
        if (text[i] === "\\") { i++; continue; }
        if (text[i] === c) break;
      }
      continue;
    }
    if (c === "(") depth++;
    else if (c === ")") {
      depth--;
      if (depth === 0) {
        args.push(text.slice(start, i));
        return args;
      }
    } else if (c === "," && depth === 1) {
      args.push(text.slice(start, i));
      start = i + 1;
    }
  }
  return null; // unclosed call
};

/* An argument that is exactly one string literal -> its unescaped value, else null. */
const literalValue = (arg) => {
  if (arg == null) return null;
  const m = /^\s*(['"])((?:\\.|(?!\1).)*)\1\s*$/.exec(arg);
  return m ? m[2].replace(/\\(['"\\])/g, "$1") : null;
};

/* Every call of `name` in `text` -> array of its argument lists. */
const callsOf = (text, name) => {
  const out = [];
  const opener = new RegExp("\\b" + name + "\\s*\\(", "g");
  let m;
  while ((m = opener.exec(text)) !== null) {
    const args = splitArgs(text, m.index + m[0].length - 1);
    if (args) out.push(args);
  }
  return out;
};

/* -> { displayNames: Set, fieldNames: Set, columnNames: Set, tables: [{ table, columns }] }
   displayNames: top-level field references by display name.
   fieldNames: legacy chip references by storage key (first path segment).
   columnNames: colField() references (context-free sub-column display names).
   tables: table aggregates — table display name + column display names. */
export const extractFormulaRefs = (formula) => {
  const refs = {
    displayNames: new Set(),
    fieldNames: new Set(),
    columnNames: new Set(),
    tables: [],
  };
  if (!formula || typeof formula !== "string") return refs;

  let m;
  CHIP_REGEX.lastIndex = 0;
  while ((m = CHIP_REGEX.exec(formula)) !== null) refs.fieldNames.add(m[1]);

  for (const args of callsOf(formula, "field")) {
    const v = literalValue(args[0]);
    if (v != null) refs.displayNames.add(v);
  }
  for (const args of callsOf(formula, "getVariable")) {
    const scope = literalValue(args[0]);
    const v = literalValue(args[1]);
    if ((scope === "Form" || scope === "Entry") && v != null) refs.displayNames.add(v);
  }
  for (const args of callsOf(formula, "colField")) {
    const v = literalValue(args[0]);
    if (v != null) refs.columnNames.add(v);
  }
  for (const name of ["sumColumn", "sumColCol", "sumColConst"]) {
    for (const args of callsOf(formula, name)) {
      const table = literalValue(args[1]);
      if (table == null) continue;
      const columns = [];
      for (let i = 2; i < args.length; i++) {
        const v = literalValue(args[i]);
        if (v != null) columns.push(v);
      }
      refs.tables.push({ table, columns });
    }
  }
  return refs;
};

/* Does this formula reference the given information field?
   field: { fieldName, displayName }. Table fields also match when a table aggregate
   names them as the table. */
export const formulaReferencesField = (formula, field) => {
  const refs = extractFormulaRefs(formula);
  if (field.displayName && refs.displayNames.has(field.displayName)) return true;
  if (field.fieldName && refs.fieldNames.has(field.fieldName)) return true;
  if (field.displayName && refs.tables.some((t) => t.table === field.displayName)) return true;
  return false;
};

/* Does this formula reference the given SUB-field (of the given parent Table/List field)?
   Sub-columns surface as colField('Sub Display') anywhere, or as a column of a table
   aggregate naming the parent table. Legacy chips carry form.parent.sub paths. */
export const formulaReferencesSubField = (formula, parentField, subField) => {
  const refs = extractFormulaRefs(formula);
  if (subField.displayName && refs.columnNames.has(subField.displayName)) return true;
  if (refs.tables.some((t) =>
    t.table === parentField.displayName &&
    t.columns.indexOf(subField.displayName) >= 0)) return true;
  if (subField.fieldName && parentField.fieldName) {
    const chipPath = new RegExp(
      "\\{\\{\\s*form\\." + parentField.fieldName + "\\." + subField.fieldName + "\\b");
    if (chipPath.test(formula)) return true;
  }
  return false;
};

export const hasChips = (formula) =>
  typeof formula === "string" && formula.indexOf("{{") >= 0;
